import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button, Col, Input, Popconfirm, Row, message } from 'antd'
import {
    ArrowRightOutlined,
    DeleteOutlined,
    HeartFilled,
    HeartOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { NAV_LINK_NAMES } from 'shared/consts'

import { CommentsService } from 'api/http'
import { CommentModel } from 'api/http/models'
import { AccountContext } from 'core/context'
import { useLanguage } from 'context/LanguageContext'

// Стили берём у родительской секции: карточки отзывов должны быть
// неотличимы от постоянных.  Локальный модуль — только для мелочей,
// которых в родителе нет (форма, дата, кнопка «показать ещё»).
import s from '../TrustSignals.module.scss'
import u from './UserReviews.module.scss'

/** На главной показываем только первые три отзыва — остальное на /comments. */
const PAGE_SIZE = 3

export type ReviewTexts = {
    feedbackText: string
    feedbackTitle: string
    feedbackButton: string
    reviewPlaceholder: string
    reviewSubmit: string
    reviewPosted: string
    reviewError: string
    reviewLoginHint: string
    reviewDelete: string
    reviewShowMore: string
    reviewTranslated: string
    reviewShowOriginal: string
    reviewAll: string
}

/**
 * Живые отзывы посетителей на главной.
 *
 * Заменяют прежнюю ссылку на внешнюю Google-форму: отзыв пишется прямо на
 * сайте, сохраняется у нас и сразу появляется в ленте рядом с постоянными
 * отзывами клиентов.  Писать могут только авторизованные (без этого пришлось
 * бы городить капчу и модерацию), удалять — автор свой и админ любой.
 *
 * Компонент отдаёт две части, потому что они живут в разных местах секции:
 * карточка-форма встаёт третьей колонкой к существующим отзывам, а лента
 * реальных отзывов идёт под ними.  Общее состояние держит родитель.
 */
export const useReviews = () => {
    const { accountData } = useContext(AccountContext)
    const { language } = useLanguage()
    const [items, setItems] = useState<CommentModel[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(false)

    const load = useCallback(
        async (nextPage: number) => {
        try {
            setLoading(true)
            // Язык сайта уходит на бэкенд: отзыв переводится на него
            // автоматически (перевод кешируется, см. shared/translate.py).
            const res = await CommentsService.getList(
                nextPage,
                PAGE_SIZE,
                language
            )
            setItems(prev =>
                nextPage === 0 ? res.items : [...prev, ...res.items]
            )
            setTotal(res.total)
            setPage(nextPage)
        } catch {
            /* лента отзывов не критична для главной — молчим */
        } finally {
            setLoading(false)
        }
        },
        [language]
    )

    // Перезапрашиваем при смене языка — тексты придут уже переведёнными.
    useEffect(() => {
        load(0)
    }, [load])

    return {
        items,
        total,
        page,
        loading,
        load,
        isAuthorized: !!accountData?.id,
    }
}

type FormProps = {
    t: ReviewTexts
    isAuthorized: boolean
    onPosted: () => void
}

/** Карточка «оставьте отзыв» — третья колонка в ряду отзывов. */
export const ReviewFormCard: React.FC<FormProps> = React.memo(
    ({ t, isAuthorized, onPosted }) => {
        const [text, setText] = useState('')
        const [sending, setSending] = useState(false)

        const submit = useCallback(async () => {
            const value = text.trim()
            if (!value) return
            try {
                setSending(true)
                await CommentsService.create(value)
                setText('')
                message.success(t.reviewPosted)
                onPosted()
            } catch (e: any) {
                message.error(e?.response?.data?.detail ?? t.reviewError)
            } finally {
                setSending(false)
            }
        }, [text, t, onPosted])

        return (
            <div className={s.feedbackCard}>
                <p className={s.feedbackText}>{t.feedbackText}</p>
                <h4>{t.feedbackTitle}</h4>

                {isAuthorized ? (
                    <>
                        <Input.TextArea
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder={t.reviewPlaceholder}
                            maxLength={2000}
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            className={u.textarea}
                        />
                        <Button
                            type="primary"
                            loading={sending}
                            disabled={!text.trim()}
                            onClick={submit}
                            className={u.submit}
                        >
                            {t.reviewSubmit}
                        </Button>
                    </>
                ) : (
                    <p className={u.loginHint}>{t.reviewLoginHint}</p>
                )}
            </div>
        )
    }
)

type ListProps = {
    t: ReviewTexts
    items: CommentModel[]
    total: number
    page: number
    loading: boolean
    onLoad: (page: number) => void
}

/** Лента реальных отзывов — карточками в том же стиле, что и постоянные. */
export const ReviewList: React.FC<ListProps> = React.memo(
    ({ t, items, total, page, loading, onLoad }) => {
        // Отзывы, для которых читатель попросил показать оригинал.
        const [showOriginal, setShowOriginal] = useState<number[]>([])
        const [liked, setLiked] = useState<Record<number, [number, boolean]>>({})
        const navigate = useNavigate()
        const { accountData } = useContext(AccountContext)

        const like = useCallback(
            async (c: CommentModel) => {
                try {
                    const res = await CommentsService.toggleLike(c.id)
                    // Обновляем только счётчик этой карточки, не дёргая ленту.
                    setLiked(prev => ({
                        ...prev,
                        [c.id]: [res.likes, res.isLiked],
                    }))
                } catch (e: any) {
                    message.error(e?.response?.data?.detail ?? t.reviewError)
                }
            },
            [t]
        )
        const remove = useCallback(
            async (id: number) => {
                try {
                    await CommentsService.remove(id)
                    onLoad(0)
                } catch (e: any) {
                    message.error(e?.response?.data?.detail ?? t.reviewError)
                }
            },
            [t, onLoad]
        )

        if (!items.length) return null

        return (
            <>
                <Row gutter={[20, 20]} className={u.list}>
                    {items.map(c => (
                        <Col xs={24} md={8} key={c.id}>
                            <div className={s.testimonial}>
                                <div className={s.quote}>
                                    {showOriginal.includes(c.id) &&
                                    c.originalText
                                        ? c.originalText
                                        : c.text}
                                </div>
                                {c.isTranslated && (
                                    <div className={u.translated}>
                                        {t.reviewTranslated}
                                        <button
                                            type="button"
                                            className={u.originalBtn}
                                            onClick={() =>
                                                setShowOriginal(prev =>
                                                    prev.includes(c.id)
                                                        ? prev.filter(
                                                              x => x !== c.id
                                                          )
                                                        : [...prev, c.id]
                                                )
                                            }
                                        >
                                            {t.reviewShowOriginal}
                                        </button>
                                    </div>
                                )}
                                <div className={s.author}>
                                    — {c.authorName}
                                    <span className={u.date}>
                                        {new Date(
                                            c.createDate
                                        ).toLocaleDateString()}
                                    </span>
                                    <Button
                                        type="text"
                                        size="small"
                                        // Свой отзыв лайкать нельзя.
                                        disabled={c.authorId === accountData?.id}
                                        onClick={() => like(c)}
                                        icon={
                                            (liked[c.id]?.[1] ?? c.isLiked) ? (
                                                <HeartFilled
                                                    className={u.liked}
                                                />
                                            ) : (
                                                <HeartOutlined />
                                            )
                                        }
                                    >
                                        {(liked[c.id]?.[0] ?? c.likes) || ''}
                                    </Button>
                                    {c.canDelete && (
                                        <Popconfirm
                                            title={t.reviewDelete}
                                            onConfirm={() => remove(c.id)}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    )}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* На главной держим только три отзыва: за остальными —
                    на отдельную страницу, там фильтры и лайки. */}
                {total > items.length && (
                    <div className={u.more}>
                        <Button
                            type="primary"
                            ghost
                            onClick={() => navigate(NAV_LINK_NAMES.COMMENTS)}
                        >
                            {t.reviewAll.replace('{n}', String(total))}{' '}
                            <ArrowRightOutlined />
                        </Button>
                    </div>
                )}
            </>
        )
    }
)
